import { CheckCircleIcon, ChefHatIcon, PackageIcon, XCircleIcon } from '@phosphor-icons/react'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button, ButtonProps } from '@/components/business/market-shop-restaurant/button'
import { Icon } from '@/components/ui/icon'
import { MarketShopOrderModel, MarketShopOrderStatus } from '@/models'
import { useMarketShopRestaurantStore } from '@/pages/market-shop-restaurant/market-shop-restaurant.store'

import * as S from './order-card.styled'

export interface OrderStatusControlsProps {
  order: MarketShopOrderModel
  size?: ButtonProps['size']
  disabled?: boolean
}

export const OrderStatusControls = ({
  order,
  size = 'medium',
  disabled,
}: OrderStatusControlsProps) => {
  const { t } = useTranslation(['market-shop-restaurant'])
  const { status } = order

  const { updateOrderStatus, updatingOrders } = useMarketShopRestaurantStore()

  const isUpdating = updatingOrders.has(order.id)

  const handleStatusClick =
    (newStatus: MarketShopOrderStatus) => (e: React.MouseEvent<HTMLButtonElement>) => {
      // NOTE: stopPropagation due to we expect status controls to be within other clickable areas
      e.stopPropagation()
      void updateOrderStatus(order, newStatus)
    }

  // NOTE: if the order is completed, we don't allow changing its status furthermore intentionally,
  // as least for now...
  if (status === 'completed') return null

  return (
    <S.Controls>
      {status === 'new' && (
        <>
          <Button
            use="secondary-danger"
            size={size}
            disabled={disabled}
            loading={isUpdating}
            onClick={handleStatusClick('declined')}
          >
            {t('market-shop-restaurant:statusControls.decline')}
            <Icon icon={XCircleIcon} size="2.4rem" />
          </Button>
          <Button
            use="primary-orange"
            size={size}
            disabled={disabled}
            loading={isUpdating}
            onClick={handleStatusClick('in_progress')}
          >
            {t('market-shop-restaurant:statusControls.start')}
            <Icon icon={ChefHatIcon} size="2.4rem" />
          </Button>
        </>
      )}

      {status === 'in_progress' && (
        <>
          <Button
            use="secondary"
            size={size}
            disabled={disabled}
            loading={isUpdating}
            onClick={handleStatusClick('new')}
          >
            {t('market-shop-restaurant:status.new')}
          </Button>
          <Button
            use="primary-blue"
            size={size}
            disabled={disabled}
            loading={isUpdating}
            onClick={handleStatusClick('awaiting_pickup')}
          >
            {t('market-shop-restaurant:statusControls.ready')}
            <Icon icon={PackageIcon} size="2.4rem" />
          </Button>
        </>
      )}

      {status === 'awaiting_pickup' && (
        <>
          <Button
            use="secondary"
            size={size}
            disabled={disabled}
            loading={isUpdating}
            onClick={handleStatusClick('in_progress')}
          >
            {t('market-shop-restaurant:status.inProgress')}
          </Button>
          <Button
            use="primary-green"
            size={size}
            disabled={disabled}
            loading={isUpdating}
            onClick={handleStatusClick('completed')}
          >
            {t('market-shop-restaurant:statusControls.complete')}
            <Icon icon={CheckCircleIcon} size="2.4rem" />
          </Button>
        </>
      )}

      {status === 'declined' && (
        <>
          <Button
            use="secondary"
            size={size}
            disabled={disabled}
            loading={isUpdating}
            onClick={handleStatusClick('new')}
          >
            {t('market-shop-restaurant:status.new')}
          </Button>
        </>
      )}
    </S.Controls>
  )
}
